"use client";

import { useMemo } from "react";
import {
  filterInlineGroups,
  type InlineComboboxGroup,
  type InlineComboboxProps,
} from "./inline-combobox-types";

interface InlineComboboxOptionsInput extends Pick<
  InlineComboboxProps,
  "groups" | "options" | "heading" | "value" | "onSearchChange"
> {
  internalQuery: string;
  highlightedValue: string | null;
}

export function useInlineComboboxOptions({
  groups,
  options,
  heading,
  value,
  onSearchChange,
  internalQuery,
  highlightedValue,
}: InlineComboboxOptionsInput) {
  const baseGroups = useMemo<InlineComboboxGroup[]>(
    () => groups ?? [{ heading: heading ?? "", options: options ?? [] }],
    [groups, options, heading],
  );
  const allOptions = useMemo(
    () => baseGroups.flatMap((group) => group.options),
    [baseGroups],
  );
  const selectedOption = useMemo(
    () => allOptions.find((option) => option.value === value) ?? null,
    [allOptions, value],
  );
  const filteredGroups = useMemo<InlineComboboxGroup[]>(
    () =>
      onSearchChange
        ? baseGroups
        : filterInlineGroups(baseGroups, internalQuery),
    [onSearchChange, baseGroups, internalQuery],
  );
  const navigableValues = useMemo(
    () =>
      filteredGroups
        .flatMap((group) => group.options)
        .filter((option) => !option.disabled)
        .map((option) => option.value),
    [filteredGroups],
  );
  const activeValue =
    highlightedValue && navigableValues.includes(highlightedValue)
      ? highlightedValue
      : (navigableValues[0] ?? null);

  return {
    allOptions,
    selectedOption,
    filteredGroups,
    navigableValues,
    activeValue,
  };
}

export function minSearchLengthMessage(
  searchesRemotely: boolean,
  minSearchLength: number,
  query: string,
): string | null {
  return searchesRemotely &&
    minSearchLength > 0 &&
    query.length < minSearchLength
    ? `Type at least ${minSearchLength} characters to search`
    : null;
}
